import { useEffect, useMemo, useState } from "react";
import { updateExternalServices } from "@/common/services/external-services.api";
import { ISupplier } from "@/types/supplier.types";
import { getAllSuppliers } from "@/common/services/supplier.api";

type ExternalServiceInitial = {
  licensePlate: string;
  description?: string;
  totalValue: number;
  supplierName: string
};

type Props = {
  id: string;
  initial: ExternalServiceInitial;
  onSubmit: () => void;
  onCancel: () => void;
};


function formatCurrencyBRFromNumber(reais: number) {
  const fixed = (Number.isFinite(reais) ? reais : 0).toFixed(2);
  return fixed.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function onlyDigitsToMaskedBR(digits: string) {
  const cents = digits.replace(/\D/g, "");
  const asNumber = (Number(cents || "0") / 100).toFixed(2);
  return asNumber.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function maskedToNumber(masked: string) {
  return parseFloat(masked.replace(/\./g, "").replace(",", "."));
}

export default function FormEditExternalServices({ id, initial, onSubmit }: Props) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    licensePlate: (initial.licensePlate).toUpperCase(),
    description: initial.description,
    totalValueMasked: formatCurrencyBRFromNumber(initial.totalValue),
    supplierName: initial.supplierName
  });

  const [suppliers, setSuppliers] = useState<ISupplier[]>([]);

  useEffect(() => {
    setForm({
      licensePlate: (initial.licensePlate).toUpperCase(),
      description: initial.description,
      totalValueMasked: formatCurrencyBRFromNumber(initial.totalValue),
      supplierName: initial.supplierName
    });

    async function load() {
      const resp = await getAllSuppliers();
      const initialSupplier = resp.data.find(sup => sup.companyName === initial.supplierName);
      if (!initialSupplier) {
        return;
      }
      setSuppliers([initialSupplier, ...resp.data.filter(resp => resp.companyName != initial.supplierName)]);
    }
    load();
  }, [id, initial]);

  const isValid = useMemo(() => {

    return Boolean(form.description?.trim() && form.licensePlate.trim() && form.totalValueMasked.trim());
  }, [form.description, form.licensePlate, form.totalValueMasked]);

  const update = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const handleTotalValueChange = (raw: string) => {
    update("totalValueMasked", onlyDigitsToMaskedBR(raw));
  };

  const handleSave = async () => {
    if (!isValid) {
      alert("Preencha Nome, Descrição e Valor Total.");
      return;
    }
    try {
      setSaving(true);
      const totalReais = maskedToNumber(form.totalValueMasked);
      const payload = {
        licensePlate: form.licensePlate.trim().toUpperCase(),
        description: form.description?.trim(),
        totalValue: totalReais,
        supplierName: form.supplierName
      };
      const resp = await updateExternalServices(id, payload);
      if (!resp?.ok) {
        console.error("Falha ao atualizar:", resp);
        alert(resp?.message || "Falha ao atualizar serviço externo.");
        return;
      }
      alert("Serviço externo atualizado com sucesso!");
      onSubmit();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error("Erro ao salvar serviço externo:", err);
      alert(err?.message || "Erro inesperado ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-w-[320px]">

      <label>Fornecedores:</label>
      <select
        style={{ minWidth: "100%", maxWidth: "100%", padding: "10px" }}
        onChange={(e) => update("supplierName", e.target.value)}
      >
        {suppliers && suppliers.map(supplier => (
          <option key={supplier._id} value={supplier.companyName}>{supplier.companyName}</option>
        ))}
      </select>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
        <LabeledInput label="Placa" value={form.licensePlate} onChange={(v) => update("licensePlate", v.toUpperCase())} />
      </div>

      <div className="mt-4">
        <LabeledTextarea label="Detalhes da O.S." value={form.description || ''} onChange={(v) => update("description", v)} rows={6} />
      </div>

      <div className="mt-6 flex items-center justify-end gap-4">
        <div className="flex items-center gap-3">
          <div className="text-sm text-neutral-600">TOTAL (R$)</div>
          <input
            type="text"
            inputMode="numeric"
            value={form.totalValueMasked}
            onChange={(e) => handleTotalValueChange(e.target.value)}
            className="min-w-[200px] text-right text-lg font-semibold tabular-nums border border-neutral-300 rounded-xl px-5 py-3 bg-neutral-50 focus:outline-none focus:ring-2"
          />
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isValid}
          className="px-5 py-2 rounded-xl bg-locavibe text-white hover:bg-neutral-800 disabled:opacity-60"
          aria-busy={saving}
        >
          {saving ? "SALVANDO…" : "SALVAR"}
        </button>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text" }: { label: string; value: string | number; onChange: (v: string) => void; type?: string; }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-sm text-neutral-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-neutral-800"
      />
    </label>
  );
}

function LabeledTextarea({ label, value, onChange, rows = 4 }: { label: string; value: string; onChange: (v: string) => void; rows?: number; }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-sm text-neutral-500">{label}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-neutral-800"
      />
    </label>
  );
}
